/**
 * useFloorEditor — the interaction orchestrator (M4/M5/M6 core).
 *
 * Owns the full gesture → state loop of the editor:
 *
 * - CAMERA (pan/pinch/double-tap) — worklets writing camera shared values;
 *   the JS thread is touched once per gesture, on settle (cull + LOD).
 * - TAP / LONG-PRESS — `scheduleOnRN` hit test (screen→world via the camera
 *   snapshot, topmost-first through the RendererRegistry hit shapes) →
 *   select / toggle.
 * - OBJECT DRAG (EDIT mode) — the drag→commit→re-record bridge: one
 *   unified pan gesture resolves its target on the JS thread at start
 *   (hit → object drag, miss → camera pan); per-frame deltas + snapping run
 *   as WORKLETS writing `dragDx/dragDy` + guide channels; gesture end
 *   commits ONE `MoveCommand` and resets the offsets in the same JS tick,
 *   so the static picture re-records exactly once per drag.
 * - MARQUEE (SELECTION mode) — worklet-drawn screen rect, resolved to an
 *   AABB-intersection selection on end.
 *
 * PENDING-frame note: the drag target resolves via `scheduleOnRN`, so the first
 * ~1 frame of a pan is a no-op until the verdict lands — imperceptible, and
 * both branches compute from gesture-start values so nothing drifts.
 *
 * @module floorplan/hooks/useFloorEditor
 * @category FloorPlan
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useCompetingGestures,
  useExclusiveGestures,
  useLongPressGesture,
  usePanGesture,
  usePinchGesture,
  useSimultaneousGestures,
  useTapGesture,
} from 'react-native-gesture-handler';
import { useSharedValue, withTiming } from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import {
  DOUBLE_TAP_ZOOM_FACTOR,
  HANDLE_TOUCH_SIZE_PX,
  lodForZoom,
  MAX_ZOOM,
  MIN_ZOOM,
  SMALL_SELECTION_BIAS_PX,
  SNAP_DISTANCE_PX,
} from '../constants';
import {
  makeDuplicateCommand,
  makeGroupCommand,
  makeInsertCommand,
  makeMoveCommand,
  makePasteCommand,
  makeRemoveCommand,
  makeTransformCommand,
  makeUngroupCommand,
  makeZOrderCommand,
  type ZOrderDirection,
} from '../history/commands';
import type { CameraState } from '../models/camera';
import { LOD } from '../models/camera';
import type { Bounds, Vec2, ViewportSize } from '../models/geometry';
import { FLOOR_MODE } from '../models/mode';
import type { FloorObject } from '../models/object';
import type { DraftChannels } from '../renderer/DraftPlane';
import type { GuideChannels } from '../renderer/GuidePlane';
import type { MarqueeChannels } from '../renderer/MarqueePlane';
import { recordFloorPicture } from '../renderer/recordPicture';
import { resolveRegistryHitShape } from '../renderer/RendererRegistry';
import type { FloorTheme, RenderContext } from '../renderer/types';
import {
  applyFrameScale,
  HANDLE_DIRECTIONS,
  hitTestHandle,
  magneticAngle,
  resizeAnchor,
  rotationPatches,
  scaleFromDelta,
} from '../selection/handles';
import {
  type AlignEdge,
  alignObjects,
  distributeObjects,
} from '../snapping/align';
import { resolveSnap, snapValueToGrid } from '../snapping/snap';
import {
  selectRenderOrderedObjects,
  useFloorEngineStore,
} from '../stores/useFloorEngineStore';
import {
  createObjectFromTool,
  nextZIndexForLayer,
  resolveTargetLayerId,
  resolveTool,
} from '../tools/ToolRegistry';
import {
  boundsIntersect,
  normalizeBounds,
  objectAabb,
  objectsAabb,
} from '../utils/bounds';
import {
  screenToWorld,
  visibleWorldBounds,
  zoomAboutPoint,
} from '../utils/camera';
import { cullObjects } from '../utils/culling';
import { hitTestTopmost } from '../utils/hitTest';
import type { FloorCamera } from '../viewport/useFloorCamera';
import {
  applyCameraState,
  cameraSnapshot,
  useFloorCamera,
  zoomCameraToBounds,
} from '../viewport/useFloorCamera';
import useFloorHistory, { type FloorHistory } from './useFloorHistory';
import useFloorPicture from './useFloorPicture';
import useFloorTheme, { type FloorThemeResult } from './useFloorTheme';
import useGridPicture from './useGridPicture';

/**
 * dragMode shared-value states — exported so chrome (the selection
 * mini-toolbar) can read the `dragMode` SV for UI-thread visibility logic.
 */
export const FLOOR_DRAG_MODE = {
  PENDING: 0,
  CAMERA: 1,
  OBJECT: 2,
  MARQUEE: 3,
  RESIZE: 4,
  ROTATE: 5,
  DRAW: 6,
} as const;

// Widened to `number` so `useSharedValue(DRAG_PENDING)` infers
// SharedValue<number>, not SharedValue<0>.
const DRAG_PENDING: number = FLOOR_DRAG_MODE.PENDING;
const DRAG_CAMERA: number = FLOOR_DRAG_MODE.CAMERA;
const DRAG_OBJECT: number = FLOOR_DRAG_MODE.OBJECT;
const DRAG_MARQUEE: number = FLOOR_DRAG_MODE.MARQUEE;
const DRAG_RESIZE: number = FLOOR_DRAG_MODE.RESIZE;
const DRAG_ROTATE: number = FLOOR_DRAG_MODE.ROTATE;
const DRAG_DRAW: number = FLOOR_DRAG_MODE.DRAW;

const COMMIT_EPSILON = 0.01;

// A press whose total movement stays under this (screen px) is treated as a tap,
// not a drag — used to recover VIEW-mode unit taps that slow / jittery simulator
// input routes to the pan gesture (VIEW mode has no press-down selection).
const TAP_MOVE_SLOP_PX = 12;

export type UseFloorEditorOptions = {
  /** Canvas layout size (screen px). Camera fitting + culling need it. */
  viewport: ViewportSize | null;
  /** Fired when a tap hits an object in ANY mode (POS live-floor hook). */
  onTapObject?: (objectId: string) => void;
  /**
   * Fired when a double-tap/double-click lands ON an object in EDIT mode —
   * the standard "enter edit" interaction (label editing). Double-tap on
   * empty canvas keeps the zoom behavior.
   */
  onEditObject?: (objectId: string) => void;
  /**
   * Per-editor overrides merged over the resolved app theme — e.g. the label
   * designer's white "paper" surface, which must not follow dark mode. Pass a
   * STABLE reference (module-level const); a fresh object per render would
   * re-record the grid/static pictures every commit.
   */
  themeOverrides?: Partial<FloorTheme>;
};

/**
 * Module-level builders (compiler-excluded tree): `revisionSignal` is the
 * change key for the imperative `getState()` reads — passed as a parameter
 * so the `useMemo` dependency is genuinely consumed.
 */
const buildSelectedPicture = (
  revisionSignal: number,
  selectedKey: string,
  ctx: RenderContext,
) => {
  if (selectedKey.length === 0 || revisionSignal < 0) return null;
  const state = useFloorEngineStore.getState();
  const selected = new Set(selectedKey.split('|'));
  const objects = selectRenderOrderedObjects(state).filter((object) =>
    selected.has(object.id),
  );
  if (objects.length === 0) return null;
  const frame = objectsAabb(objects);
  if (frame === null) return null;
  return recordFloorPicture(objects, ctx, frame);
};

const buildSelectionFrame = (
  revisionSignal: number,
  selectedKey: string,
): Bounds | null => {
  if (selectedKey.length === 0 || revisionSignal < 0) return null;
  const state = useFloorEngineStore.getState();
  const objects: FloorObject[] = [];
  for (const id of selectedKey.split('|')) {
    const object = state.objectsById[id];
    if (object !== undefined) objects.push(object);
  }
  return objectsAabb(objects);
};

const useFloorEditor = (options: UseFloorEditorOptions) => {
  const { viewport, onTapObject, onEditObject, themeOverrides } = options;
  const baseAppearance: FloorThemeResult = useFloorTheme();
  // Depend on the stable inner fields — the wrapper object from useFloorTheme
  // is a fresh literal per render and would re-record the grid every commit.
  const baseTheme = baseAppearance.theme;
  const baseFont = baseAppearance.font;
  const appearance: FloorThemeResult = useMemo(() => {
    if (themeOverrides === undefined) {
      return { theme: baseTheme, font: baseFont };
    }
    return { theme: { ...baseTheme, ...themeOverrides }, font: baseFont };
  }, [baseTheme, baseFont, themeOverrides]);
  const camera: FloorCamera = useFloorCamera();
  const history: FloorHistory = useFloorHistory();

  // ── Pictures ─────────────────────────────────────────────────────────
  const gridPicture = useGridPicture(appearance.theme);
  const staticPicture = useFloorPicture(appearance);
  const revision = useFloorEngineStore((s) => s.revision);
  const selectedKey = useFloorEngineStore((s) => s.selectedIds.join('|'));
  const { theme, font } = appearance;

  /** Selected objects rendered at full LOD in the dynamic plane. */
  const selectedPicture = useMemo(
    () =>
      buildSelectedPicture(revision, selectedKey, {
        lod: LOD.CLOSE,
        theme,
        font,
      }),
    [selectedKey, revision, theme, font],
  );

  /** Selection frame (union AABB) for outline + handles. */
  const selectionFrame = useMemo<Bounds | null>(
    () => buildSelectionFrame(revision, selectedKey),
    [selectedKey, revision],
  );

  // ── Shared values (UI-thread interaction state) ──────────────────────
  const dragMode = useSharedValue(DRAG_PENDING);
  const dragDx = useSharedValue(0);
  const dragDy = useSharedValue(0);
  const dragBounds = useSharedValue<Bounds | null>(null);
  const candidates = useSharedValue<Bounds[]>([]);
  const startTx = useSharedValue(0);
  const startTy = useSharedValue(0);
  const startZoom = useSharedValue(1);
  // Touch-DOWN point, captured in onBegin. Pan onStart fires at ACTIVATION
  // (after the min-distance threshold), so its event.x/y has already moved
  // off the pressed point — hit-testing there misses the object under the
  // finger (routes the drag to camera-pan) and offsets draw/marquee origins.
  const beginX = useSharedValue(0);
  const beginY = useSharedValue(0);
  // Keyboard modifier state (1 = held). Written by useFloorKeyboard on web
  // (hardware keyboards); always 0 on touch, so every branch below is a
  // no-op on mobile. Shift = multi-select / axis-lock / aspect-lock,
  // Alt = duplicate-drag / resize-from-center, Space = hand-tool pan.
  const shiftSv = useSharedValue(0);
  const altSv = useSharedValue(0);
  const spaceSv = useSharedValue(0);
  // Snap config mirrored into SVs (worklets can't read Zustand).
  const gridEnabledSv = useSharedValue(1);
  const snapEnabledSv = useSharedValue(1);
  const gridSizeSv = useSharedValue(20);
  const marqueeModeSv = useSharedValue(0);
  // Resize/rotate interaction state (written at resolve, read per frame).
  const anchorX = useSharedValue(0);
  const anchorY = useSharedValue(0);
  const dirX = useSharedValue(0);
  const dirY = useSharedValue(0);
  const dragSx = useSharedValue(1);
  const dragSy = useSharedValue(1);
  const rotCenterX = useSharedValue(0);
  const rotCenterY = useSharedValue(0);
  const rotStartAngle = useSharedValue(0);
  const dragRotation = useSharedValue(0);
  // DRAW-mode state.
  const drawModeSv = useSharedValue(0);
  const draftActive = useSharedValue(0);
  const draftX = useSharedValue(0);
  const draftY = useSharedValue(0);
  const draftWidth = useSharedValue(0);
  const draftHeight = useSharedValue(0);
  const draft: DraftChannels = {
    active: draftActive,
    x: draftX,
    y: draftY,
    width: draftWidth,
    height: draftHeight,
  };

  const guideXActive = useSharedValue(0);
  const guideXPosition = useSharedValue(0);
  const guideXFrom = useSharedValue(0);
  const guideXTo = useSharedValue(0);
  const guideYActive = useSharedValue(0);
  const guideYPosition = useSharedValue(0);
  const guideYFrom = useSharedValue(0);
  const guideYTo = useSharedValue(0);
  const guides: GuideChannels = {
    xActive: guideXActive,
    xPosition: guideXPosition,
    xFrom: guideXFrom,
    xTo: guideXTo,
    yActive: guideYActive,
    yPosition: guideYPosition,
    yFrom: guideYFrom,
    yTo: guideYTo,
  };

  const marquee: MarqueeChannels = {
    active: useSharedValue(0),
    startX: useSharedValue(0),
    startY: useSharedValue(0),
    currentX: useSharedValue(0),
    currentY: useSharedValue(0),
  };

  // Mirror store mode + snap toggles into SVs.
  const viewModeSv = useSharedValue(0);
  const mode = useFloorEngineStore((s) => s.mode);
  const gridEnabled = useFloorEngineStore((s) => s.gridEnabled);
  const snapEnabled = useFloorEngineStore((s) => s.snapEnabled);
  const gridSize = useFloorEngineStore((s) => s.meta?.grid?.size ?? 20);
  useEffect(() => {
    viewModeSv.set(mode === FLOOR_MODE.VIEW ? 1 : 0);
    marqueeModeSv.set(mode === FLOOR_MODE.SELECTION ? 1 : 0);
    drawModeSv.set(mode === FLOOR_MODE.DRAW ? 1 : 0);
    gridEnabledSv.set(gridEnabled ? 1 : 0);
    snapEnabledSv.set(snapEnabled ? 1 : 0);
    gridSizeSv.set(gridSize);
  }, [
    mode,
    gridEnabled,
    snapEnabled,
    gridSize,
    viewModeSv,
    marqueeModeSv,
    drawModeSv,
    gridEnabledSv,
    snapEnabledSv,
    gridSizeSv,
  ]);

  // Dragged-id list lives in a shared value (NOT a ref): it's written by the
  // JS resolve/commit pair and read only there, and SVs pass the render-
  // immutability lint where a ref capture inside gesture closures does not.
  const draggedIds = useSharedValue<string[]>([]);
  const [zoomSnapshot, setZoomSnapshot] = useState(1);

  // ── JS-thread callbacks (one call per gesture, never per frame) ──────
  // Deliberately PLAIN functions, not useCallback: this tree is React-
  // Compiler-excluded, the gesture builders rebuild per render anyway, and
  // memoized closures that .set() their own dependencies trip the
  // `preserve-manual-memoization` lint. Nothing downstream keys on identity.
  const handleSettle = (state: CameraState) => {
    setZoomSnapshot(state.zoom);
    if (viewport === null) return;
    const store = useFloorEngineStore.getState();
    store.setCullBounds(visibleWorldBounds(state, viewport));
    store.setLod(lodForZoom(state.zoom));
  };

  /**
   * Arm a DRAG_OBJECT session for `ids` (unlocked members only): stash the
   * dragged set + its AABB + the snap-candidate neighbor AABBs, then flip
   * dragMode. Returns false (without touching dragMode) when nothing in
   * `ids` is draggable. Shared by the canvas pan's hit-test path AND the
   * selection-toolbar drag handle.
   */
  const beginObjectDrag = (ids: readonly string[]): boolean => {
    const store = useFloorEngineStore.getState();
    const dragged: FloorObject[] = [];
    for (const id of ids) {
      const object = store.objectsById[id];
      if (object !== undefined && !object.locked) dragged.push(object);
    }
    if (dragged.length === 0) return false;
    draggedIds.set(dragged.map((object) => object.id));
    dragBounds.set(objectsAabb(dragged));
    const draggedSet = new Set(draggedIds.get());
    const ordered = selectRenderOrderedObjects(store);
    const neighborAabbs: Bounds[] = [];
    for (const object of cullObjects(ordered, store.cullBounds)) {
      if (draggedSet.has(object.id)) continue;
      neighborAabbs.push(objectAabb(object));
    }
    candidates.set(neighborAabbs);
    dragMode.set(DRAG_OBJECT);
    return true;
  };

  /**
   * Selection-toolbar drag-handle entry: drag the CURRENT selection with no
   * hit test (the handle is a dedicated affordance — no ambiguity to
   * resolve). Locked-only selections no-op (dragMode stays PENDING, so the
   * handle gesture's update/commit branches never fire).
   */
  const beginSelectionHandleDrag = () => {
    const store = useFloorEngineStore.getState();
    if (store.mode !== FLOOR_MODE.EDIT) return;
    if (store.selectedIds.length === 0) return;
    beginObjectDrag(store.selectedIds);
  };

  /**
   * Drag-handle gesture end (JS side, ordered after beginSelectionHandleDrag
   * via the scheduleOnRN queue): commit + reset iff a handle drag actually armed.
   */
  const finishSelectionHandleDrag = () => {
    if (dragMode.get() !== DRAG_OBJECT) return;
    commitDrag();
  };

  const resolveDragTarget = (x: number, y: number) => {
    const store = useFloorEngineStore.getState();
    // Space+drag = temporary hand tool in ANY mode (Figma/Photoshop).
    if (spaceSv.get() === 1) {
      dragMode.set(DRAG_CAMERA);
      return;
    }
    if (store.mode !== FLOOR_MODE.EDIT) {
      dragMode.set(DRAG_CAMERA);
      return;
    }
    const cam = cameraSnapshot(camera);
    // Handles first — they float OUTSIDE object bounds, so they must win
    // over the object hit-test.
    if (store.selectedIds.length > 0) {
      const selectedObjects: FloorObject[] = [];
      for (const id of store.selectedIds) {
        const object = store.objectsById[id];
        if (object !== undefined) selectedObjects.push(object);
      }
      const frame = objectsAabb(selectedObjects);
      if (frame !== null) {
        let handle = hitTestHandle({ x, y }, frame, cam, HANDLE_TOUCH_SIZE_PX);
        // Small-selection drag bias: on a tiny selection the 8 resize
        // handles' touch targets blanket the whole object body, so every
        // drag became a resize. Ignore a non-rotate handle hit landing
        // INSIDE the frame body — the touch falls through to the object
        // drag. The rotate handle floats outside the frame and the handle
        // halves poking past the edges still resize.
        const frameWpx = frame.width * cam.zoom;
        const frameHpx = frame.height * cam.zoom;
        if (
          handle !== null &&
          handle !== 'rotate' &&
          Math.min(frameWpx, frameHpx) < SMALL_SELECTION_BIAS_PX
        ) {
          const leftPx = frame.x * cam.zoom + cam.tx;
          const topPx = frame.y * cam.zoom + cam.ty;
          const insideBody =
            x > leftPx + 2 &&
            x < leftPx + frameWpx - 2 &&
            y > topPx + 2 &&
            y < topPx + frameHpx - 2;
          if (insideBody) handle = null;
        }
        if (handle !== null) {
          const dragged = selectedObjects.filter((object) => !object.locked);
          if (dragged.length > 0) {
            draggedIds.set(dragged.map((object) => object.id));
            dragBounds.set(frame);
            if (handle === 'rotate') {
              const cx = frame.x + frame.width / 2;
              const cy = frame.y + frame.height / 2;
              rotCenterX.set(cx);
              rotCenterY.set(cy);
              const world = screenToWorld({ x, y }, cam);
              rotStartAngle.set(Math.atan2(world.y - cy, world.x - cx));
              dragRotation.set(0);
              dragMode.set(DRAG_ROTATE);
              return;
            }
            const dirs = HANDLE_DIRECTIONS[handle];
            // Alt = resize from center (Figma/Sketch); read at grab time.
            const anchor =
              altSv.get() === 1
                ? {
                    x: frame.x + frame.width / 2,
                    y: frame.y + frame.height / 2,
                  }
                : resizeAnchor(frame, handle);
            anchorX.set(anchor.x);
            anchorY.set(anchor.y);
            dirX.set(dirs.x);
            dirY.set(dirs.y);
            dragSx.set(1);
            dragSy.set(1);
            dragMode.set(DRAG_RESIZE);
            return;
          }
        }
      }
    }
    const world: Vec2 = screenToWorld({ x, y }, cam);
    const ordered = selectRenderOrderedObjects(store);
    const hit = hitTestTopmost(world, ordered, {
      resolveShape: resolveRegistryHitShape,
    });
    if (hit === null) {
      dragMode.set(DRAG_CAMERA);
      return;
    }
    let ids = store.selectedIds;
    if (!ids.includes(hit.id)) {
      store.setSelectedIds([hit.id]);
      ids = [hit.id];
    }
    if (!beginObjectDrag(ids)) {
      dragMode.set(DRAG_CAMERA);
    }
  };

  /**
   * Alt+drag = duplicate-and-drag (Figma/Illustrator). Runs at pan
   * ACTIVATION when the resolved target is an object drag: clones the
   * dragged set IN PLACE (zero offset — the drag itself supplies the
   * displacement), selects the clones, and re-points the live drag at them.
   * The insert is its own history entry; the move commits separately on
   * gesture end — matching the two-step undo Figma records.
   */
  const beginAltDuplicate = () => {
    if (dragMode.get() !== DRAG_OBJECT) return;
    const store = useFloorEngineStore.getState();
    const ids = draggedIds.get();
    if (ids.length === 0) return;
    const result = makeDuplicateCommand(store.objectsById, ids, {
      x: 0,
      y: 0,
    });
    if (result === null) return;
    history.execute(result.command);
    store.setSelectedIds(result.newIds);
    draggedIds.set(result.newIds);
  };

  const commitDrag = () => {
    const dx = dragDx.get();
    const dy = dragDy.get();
    const ids = draggedIds.get();
    if (
      ids.length > 0 &&
      (Math.abs(dx) > COMMIT_EPSILON || Math.abs(dy) > COMMIT_EPSILON)
    ) {
      const store = useFloorEngineStore.getState();
      const command = makeMoveCommand(store.objectsById, ids, { x: dx, y: dy });
      if (command !== null) history.execute(command);
    }
    // Reset in the SAME tick as the store patch: the next frame paints the
    // re-recorded pictures AND the zeroed offset together (no double-move).
    draggedIds.set([]);
    dragDx.set(0);
    dragDy.set(0);
    dragBounds.set(null);
    guideXActive.set(0);
    guideYActive.set(0);
    dragMode.set(DRAG_PENDING);
  };

  const commitResize = () => {
    const sx = dragSx.get();
    const sy = dragSy.get();
    const ids = draggedIds.get();
    if (
      ids.length > 0 &&
      (Math.abs(sx - 1) > 0.0001 || Math.abs(sy - 1) > 0.0001)
    ) {
      const store = useFloorEngineStore.getState();
      const anchor = { x: anchorX.get(), y: anchorY.get() };
      const entries: {
        id: string;
        next: { x: number; y: number; width: number; height: number };
      }[] = [];
      for (const id of ids) {
        const object = store.objectsById[id];
        if (object === undefined) continue;
        entries.push({ id, next: applyFrameScale(object, anchor, sx, sy) });
      }
      const command = makeTransformCommand(
        store.objectsById,
        entries,
        `Resize ${entries.length}`,
      );
      if (command !== null) history.execute(command);
    }
    draggedIds.set([]);
    dragSx.set(1);
    dragSy.set(1);
    dragBounds.set(null);
    dragMode.set(DRAG_PENDING);
  };

  const commitRotate = () => {
    const delta = dragRotation.get();
    const ids = draggedIds.get();
    if (ids.length > 0 && Math.abs(delta) > 0.05) {
      const store = useFloorEngineStore.getState();
      const objects: FloorObject[] = [];
      for (const id of ids) {
        const object = store.objectsById[id];
        if (object !== undefined) objects.push(object);
      }
      const patches = rotationPatches(
        objects,
        { x: rotCenterX.get(), y: rotCenterY.get() },
        delta,
      );
      const command = makeTransformCommand(
        store.objectsById,
        patches,
        `Rotate ${patches.length}`,
      );
      if (command !== null) history.execute(command);
    }
    draggedIds.set([]);
    dragRotation.set(0);
    dragBounds.set(null);
    dragMode.set(DRAG_PENDING);
  };

  /** Create ONE object from the active tool over a world bounds. */
  const createFromActiveTool = (bounds: Bounds): void => {
    const store = useFloorEngineStore.getState();
    const tool = resolveTool(store.activeTool);
    if (tool === null) return;
    const layerId = resolveTargetLayerId(store);
    if (layerId === null) return;
    const zIndex = nextZIndexForLayer(store, layerId);
    const object = createObjectFromTool(tool, bounds, { layerId, zIndex });
    history.execute(makeInsertCommand([object], `Add ${tool.id}`));
    store.setSelectedIds([object.id]);
    // Revert to the move tool after a create (Figma/Sketch standard): the
    // fresh object is selected and immediately draggable — repeat drawing
    // re-arms the tool via the chip or its hotkey.
    store.setMode(FLOOR_MODE.EDIT);
  };

  /** Tap placement: the tool's default size centered on the point. */
  const placeAtPoint = (worldX: number, worldY: number): void => {
    const store = useFloorEngineStore.getState();
    const tool = resolveTool(store.activeTool);
    if (tool === null) return;
    let x = worldX - tool.defaultSize.width / 2;
    let y = worldY - tool.defaultSize.height / 2;
    if (store.gridEnabled) {
      const grid = store.meta?.grid?.size ?? 0;
      x = snapValueToGrid(x, grid);
      y = snapValueToGrid(y, grid);
    }
    createFromActiveTool({
      x,
      y,
      width: tool.defaultSize.width,
      height: tool.defaultSize.height,
    });
  };

  /** Drag-create commit: drawn bounds, falling back to tap for tiny drags. */
  const commitDraftCreate = (): void => {
    const store = useFloorEngineStore.getState();
    const tool = resolveTool(store.activeTool);
    const rect = normalizeBounds({
      x: draftX.get(),
      y: draftY.get(),
      width: draftWidth.get(),
      height: draftHeight.get(),
    });
    draftActive.set(0);
    draftWidth.set(0);
    draftHeight.set(0);
    dragMode.set(DRAG_PENDING);
    if (tool === null) return;
    const minSize = tool.minSize ?? 8;
    if (!tool.dragToSize || (rect.width < minSize && rect.height < minSize)) {
      placeAtPoint(rect.x + rect.width / 2, rect.y + rect.height / 2);
      return;
    }
    createFromActiveTool(rect);
  };

  const commitMarquee = (sx: number, sy: number, cx: number, cy: number) => {
    const store = useFloorEngineStore.getState();
    const cam = cameraSnapshot(camera);
    const a = screenToWorld({ x: sx, y: sy }, cam);
    const b = screenToWorld({ x: cx, y: cy }, cam);
    const rect = normalizeBounds({
      x: a.x,
      y: a.y,
      width: b.x - a.x,
      height: b.y - a.y,
    });
    const ids: string[] = [];
    for (const object of selectRenderOrderedObjects(store)) {
      if (object.locked) continue;
      if (boundsIntersect(objectAabb(object), rect)) ids.push(object.id);
    }
    store.setSelectedIds(ids);
  };

  const handleTap = (x: number, y: number) => {
    // Space = hand tool; clicks pan, never select (Figma/Photoshop).
    if (spaceSv.get() === 1) return;
    const store = useFloorEngineStore.getState();
    const cam = cameraSnapshot(camera);
    const world = screenToWorld({ x, y }, cam);
    if (store.mode === FLOOR_MODE.DRAW) {
      placeAtPoint(world.x, world.y);
      return;
    }
    const hit = hitTestTopmost(world, selectRenderOrderedObjects(store), {
      resolveShape: resolveRegistryHitShape,
    });
    if (hit !== null && onTapObject !== undefined) onTapObject(hit.id);
    // VIEW is read-only; PAN (hand tool) is navigation-only — neither mutates
    // the selection. Selecting in PAN was misleading: the outline appeared but
    // every drag still panned the camera (hand-tool semantics), reading as
    // "dragging pans even though the object is selected".
    if (store.mode === FLOOR_MODE.VIEW || store.mode === FLOOR_MODE.PAN) return;
    const shiftHeld = shiftSv.get() === 1;
    if (hit === null) {
      // Shift+click on empty space keeps the selection (Figma).
      if (!shiftHeld) store.clearSelection();
      return;
    }
    // Shift+click toggles membership (standard multi-select).
    if (shiftHeld) {
      store.toggleSelection(hit.id);
      return;
    }
    // Group expansion: tapping any member selects the whole group.
    let groupId = hit.groupId;
    if (groupId === undefined && hit.type === 'group') groupId = hit.id;
    if (groupId !== undefined) {
      const ids: string[] = [];
      if (store.objectsById[groupId] !== undefined) ids.push(groupId);
      for (const object of Object.values(store.objectsById)) {
        if (object.groupId === groupId) ids.push(object.id);
      }
      store.setSelectedIds(ids);
      return;
    }
    store.setSelectedIds([hit.id]);
  };

  const handleLongPress = (x: number, y: number) => {
    const store = useFloorEngineStore.getState();
    // Alternate-select is an editing affordance — inert in the read-only VIEW
    // mode AND the navigation-only PAN (hand) mode, matching handleTap.
    if (store.mode === FLOOR_MODE.VIEW || store.mode === FLOOR_MODE.PAN) return;
    const cam = cameraSnapshot(camera);
    const world = screenToWorld({ x, y }, cam);
    // skipLocked: false — long-press is the alternate-select gesture and the
    // ONLY way to select a locked object (tap/pan skip locked), which is what
    // makes the selection toolbar's Unlock action reachable.
    const hit = hitTestTopmost(world, selectRenderOrderedObjects(store), {
      resolveShape: resolveRegistryHitShape,
      skipLocked: false,
    });
    if (hit !== null) store.toggleSelection(hit.id);
  };

  // ── Gestures ─────────────────────────────────────────────────────────

  /**
   * Per-frame DRAG_OBJECT math (UI thread): screen translation → world
   * delta (÷zoom), shift axis-lock, snap resolve, dragDx/dragDy + guide
   * channel writes. Shared by the canvas pan AND the selection-toolbar
   * drag-handle gesture so the two paths can never drift.
   */
  const updateObjectDrag = (translationX: number, translationY: number) => {
    'worklet';

    const base = dragBounds.get();
    if (base === null) return;
    const zoom = camera.zoom.get();
    let tX = translationX;
    let tY = translationY;
    // Shift = axis-lock to the dominant direction (Figma/Illustrator).
    if (shiftSv.get() === 1) {
      if (Math.abs(tX) >= Math.abs(tY)) {
        tY = 0;
      } else {
        tX = 0;
      }
    }
    const rawDx = tX / zoom;
    const rawDy = tY / zoom;
    const moving: Bounds = {
      x: base.x + rawDx,
      y: base.y + rawDy,
      width: base.width,
      height: base.height,
    };
    const snap = resolveSnap(moving, {
      candidates: candidates.get(),
      tolerance: SNAP_DISTANCE_PX / zoom,
      gridEnabled: gridEnabledSv.get() === 1,
      gridSize: gridSizeSv.get(),
      objectSnapEnabled: snapEnabledSv.get() === 1,
    });
    dragDx.set(rawDx + snap.dx);
    dragDy.set(rawDy + snap.dy);
    let xActive = 0;
    let yActive = 0;
    for (const guide of snap.guides) {
      if (guide.axis === 'x') {
        xActive = 1;
        guides.xPosition.set(guide.position);
        guides.xFrom.set(guide.from);
        guides.xTo.set(guide.to);
      } else {
        yActive = 1;
        guides.yPosition.set(guide.position);
        guides.yFrom.set(guide.from);
        guides.yTo.set(guide.to);
      }
    }
    guides.xActive.set(xActive);
    guides.yActive.set(yActive);
  };

  const pan = usePanGesture({
    maxPointers: 1,
    onBegin: (event) => {
      'worklet';

      // Touch-down: capture the true press point and kick the JS hit-test
      // NOW — by pan activation the finger has already moved off it.
      beginX.set(event.x);
      beginY.set(event.y);
      if (drawModeSv.get() === 1 || marqueeModeSv.get() === 1) return;
      dragMode.set(DRAG_PENDING);
      scheduleOnRN(resolveDragTarget, event.x, event.y);
    },
    onActivate: () => {
      'worklet';

      startTx.set(camera.tx.get());
      startTy.set(camera.ty.get());
      // Alt+drag on an object: clone in place and drag the clones.
      if (altSv.get() === 1 && dragMode.get() === DRAG_OBJECT) {
        scheduleOnRN(beginAltDuplicate);
      }
      if (drawModeSv.get() === 1) {
        const zoom = camera.zoom.get();
        let worldX = (beginX.get() - camera.tx.get()) / zoom;
        let worldY = (beginY.get() - camera.ty.get()) / zoom;
        if (gridEnabledSv.get() === 1) {
          worldX = snapValueToGrid(worldX, gridSizeSv.get());
          worldY = snapValueToGrid(worldY, gridSizeSv.get());
        }
        draftActive.set(1);
        draftX.set(worldX);
        draftY.set(worldY);
        draftWidth.set(0);
        draftHeight.set(0);
        dragMode.set(DRAG_DRAW);
        return;
      }
      if (marqueeModeSv.get() === 1) {
        dragMode.set(DRAG_MARQUEE);
        marquee.active.set(1);
        marquee.startX.set(beginX.get());
        marquee.startY.set(beginY.get());
        marquee.currentX.set(beginX.get());
        marquee.currentY.set(beginY.get());
      }
    },
    onUpdate: (event) => {
      'worklet';

      if (dragMode.get() === DRAG_DRAW) {
        const zoom = camera.zoom.get();
        let worldX = (event.x - camera.tx.get()) / zoom;
        let worldY = (event.y - camera.ty.get()) / zoom;
        if (gridEnabledSv.get() === 1) {
          worldX = snapValueToGrid(worldX, gridSizeSv.get());
          worldY = snapValueToGrid(worldY, gridSizeSv.get());
        }
        draftWidth.set(worldX - draftX.get());
        draftHeight.set(worldY - draftY.get());
        return;
      }
      if (dragMode.get() === DRAG_MARQUEE) {
        marquee.currentX.set(event.x);
        marquee.currentY.set(event.y);
        return;
      }
      if (dragMode.get() === DRAG_CAMERA) {
        camera.tx.set(startTx.get() + event.translationX);
        camera.ty.set(startTy.get() + event.translationY);
        return;
      }
      if (dragMode.get() === DRAG_OBJECT) {
        updateObjectDrag(event.translationX, event.translationY);
        return;
      }
      if (dragMode.get() === DRAG_RESIZE) {
        const base = dragBounds.get();
        if (base === null) return;
        const zoom = camera.zoom.get();
        const dxw = event.translationX / zoom;
        const dyw = event.translationY / zoom;
        let sx = scaleFromDelta(base.width, dirX.get(), dxw);
        let sy = scaleFromDelta(base.height, dirY.get(), dyw);
        // Shift on a corner handle = maintain aspect ratio: the axis pulled
        // furthest from 1 wins and drives both (Figma behavior).
        if (shiftSv.get() === 1 && dirX.get() !== 0 && dirY.get() !== 0) {
          const uniform = Math.abs(1 - sx) >= Math.abs(1 - sy) ? sx : sy;
          sx = uniform;
          sy = uniform;
        }
        dragSx.set(sx);
        dragSy.set(sy);
        return;
      }
      if (dragMode.get() === DRAG_ROTATE) {
        const zoom = camera.zoom.get();
        const wx = (event.x - camera.tx.get()) / zoom;
        const wy = (event.y - camera.ty.get()) / zoom;
        const angle = Math.atan2(wy - rotCenterY.get(), wx - rotCenterX.get());
        const deltaDeg = ((angle - rotStartAngle.get()) * 180) / Math.PI;
        // Shift FORCES the 15° snap even with magnetic snapping off.
        dragRotation.set(
          magneticAngle(
            deltaDeg,
            snapEnabledSv.get() === 1 || shiftSv.get() === 1,
          ),
        );
        return;
      }
      // DRAG_PENDING: hold until the JS verdict lands (~1 frame).
    },
    onDeactivate: (event) => {
      'worklet';

      if (dragMode.get() === DRAG_DRAW) {
        scheduleOnRN(commitDraftCreate);
        return;
      }
      if (dragMode.get() === DRAG_MARQUEE) {
        marquee.active.set(0);
        scheduleOnRN(
          commitMarquee,
          marquee.startX.get(),
          marquee.startY.get(),
          event.x,
          event.y,
        );
        dragMode.set(DRAG_PENDING);
        return;
      }
      if (dragMode.get() === DRAG_OBJECT) {
        scheduleOnRN(commitDrag);
        return;
      }
      if (dragMode.get() === DRAG_RESIZE) {
        scheduleOnRN(commitResize);
        return;
      }
      if (dragMode.get() === DRAG_ROTATE) {
        scheduleOnRN(commitRotate);
        return;
      }
      // VIEW mode has no press-down selection, so a no-movement camera press is
      // really a tap — recover it here (the standalone tap gesture can lose to
      // this pan on slow / jittery simulator input). EDIT is untouched.
      if (
        viewModeSv.get() === 1 &&
        Math.abs(event.translationX) < TAP_MOVE_SLOP_PX &&
        Math.abs(event.translationY) < TAP_MOVE_SLOP_PX
      ) {
        scheduleOnRN(handleTap, event.x, event.y);
      }
      // Camera (or unresolved pending) → settle.
      const snapshot: CameraState = {
        tx: camera.tx.get(),
        ty: camera.ty.get(),
        zoom: camera.zoom.get(),
      };
      dragMode.set(DRAG_PENDING);
      scheduleOnRN(handleSettle, snapshot);
    },
  });

  const pinch = usePinchGesture({
    onActivate: () => {
      'worklet';

      startTx.set(camera.tx.get());
      startTy.set(camera.ty.get());
      startZoom.set(camera.zoom.get());
    },
    onUpdate: (event) => {
      'worklet';

      let nextZoom = startZoom.get() * event.scale;
      if (nextZoom < MIN_ZOOM) nextZoom = MIN_ZOOM;
      if (nextZoom > MAX_ZOOM) nextZoom = MAX_ZOOM;
      const worldX = (event.focalX - startTx.get()) / startZoom.get();
      const worldY = (event.focalY - startTy.get()) / startZoom.get();
      camera.zoom.set(nextZoom);
      camera.tx.set(event.focalX - worldX * nextZoom);
      camera.ty.set(event.focalY - worldY * nextZoom);
    },
    onDeactivate: () => {
      'worklet';

      const snapshot: CameraState = {
        tx: camera.tx.get(),
        ty: camera.ty.get(),
        zoom: camera.zoom.get(),
      };
      scheduleOnRN(handleSettle, snapshot);
    },
  });

  /**
   * Double-tap decision runs FULLY on JS (the hit test needs the store):
   * on an object in EDIT mode → select + enter edit (label dialog), NO
   * zoom; on empty canvas / locked object / other modes → the animated
   * zoom step about the tap point. Shared-value writes with withTiming
   * from the JS thread are supported, so the worklet just forwards.
   */
  const handleDoubleTap = (x: number, y: number): void => {
    const store = useFloorEngineStore.getState();
    if (store.mode === FLOOR_MODE.EDIT && onEditObject !== undefined) {
      const cam = cameraSnapshot(camera);
      const world = screenToWorld({ x, y }, cam);
      const hit = hitTestTopmost(world, selectRenderOrderedObjects(store), {
        resolveShape: resolveRegistryHitShape,
      });
      if (hit !== null && !hit.locked) {
        store.setSelectedIds([hit.id]);
        onEditObject(hit.id);
        return;
      }
    }
    const currentZoom = camera.zoom.get();
    let nextZoom = currentZoom * DOUBLE_TAP_ZOOM_FACTOR;
    if (nextZoom > MAX_ZOOM) nextZoom = MAX_ZOOM;
    const worldX = (x - camera.tx.get()) / currentZoom;
    const worldY = (y - camera.ty.get()) / currentZoom;
    const duration = 220;
    const targetTx = x - worldX * nextZoom;
    const targetTy = y - worldY * nextZoom;
    camera.zoom.set(withTiming(nextZoom, { duration }));
    camera.tx.set(withTiming(targetTx, { duration }));
    camera.ty.set(withTiming(targetTy, { duration }));
    handleSettle({ tx: targetTx, ty: targetTy, zoom: nextZoom });
  };

  const doubleTap = useTapGesture({
    numberOfTaps: 2,
    maxDuration: 260,
    onDeactivate: (event) => {
      'worklet';

      scheduleOnRN(handleDoubleTap, event.x, event.y);
    },
  });

  const tap = useTapGesture({
    numberOfTaps: 1,
    maxDuration: 300,
    onDeactivate: (event) => {
      'worklet';

      scheduleOnRN(handleTap, event.x, event.y);
    },
  });

  const longPress = useLongPressGesture({
    minDuration: 350,
    onActivate: (event) => {
      'worklet';

      scheduleOnRN(handleLongPress, event.x, event.y);
    },
  });

  // Exclusive, NOT Race, for the tap family: in a Race the single tap
  // ACTIVATES on first release and the double-tap recognizer can never win.
  // Exclusive gives double-tap priority — the single tap waits out the
  // double-tap window (~300ms). Selection still FEELS instant because the
  // pan's onBegin hit-test already selects on press-down.
  // Hooks cannot be nested as arguments, so each composition is its own call.
  const pointerGestures = useExclusiveGestures(doubleTap, longPress, tap);
  const cameraGestures = useSimultaneousGestures(pan, pinch);
  const gesture = useCompetingGestures(pointerGestures, cameraGestures);

  /**
   * Pan gesture for the floating selection-toolbar drag handle. Built HERE
   * (compiler-excluded tree) and handed to the chrome prebuilt, so the
   * compiled Editor never authors gesture worklets. Drives the exact same
   * live-drag pipeline as the canvas pan (beginObjectDrag → updateObjectDrag
   * → commitDrag) with no hit test — the handle drags the current selection.
   */
  const selectionDragGesture = usePanGesture({
    maxPointers: 1,
    minDistance: 1,
    shouldCancelWhenOutside: false,
    onBegin: () => {
      'worklet';

      scheduleOnRN(beginSelectionHandleDrag);
    },
    onUpdate: (event) => {
      'worklet';

      if (dragMode.get() !== DRAG_OBJECT) return;
      updateObjectDrag(event.translationX, event.translationY);
    },
    onFinalize: () => {
      'worklet';

      // onFinalize (not onEnd) so a cancelled gesture still commits/resets.
      // Unconditional + JS-side dragMode check: scheduleOnRN callbacks run in
      // order on the JS thread, so this always lands AFTER onBegin's
      // beginSelectionHandleDrag — a tap-without-drag can't leak an armed
      // DRAG_OBJECT state. commitDrag with a sub-epsilon delta is a safe
      // reset.
      scheduleOnRN(finishSelectionHandleDrag);
    },
  });

  // ── Imperative ops ───────────────────────────────────────────────────
  const fit = () => {
    if (viewport === null) return;
    const store = useFloorEngineStore.getState();
    const meta = store.meta;
    if (meta === null) return;
    const state = zoomCameraToBounds(
      camera,
      { x: 0, y: 0, width: meta.width, height: meta.height },
      viewport,
    );
    handleSettle(state);
  };

  const zoomToSelection = () => {
    if (viewport === null || selectionFrame === null) return;
    const state = zoomCameraToBounds(camera, selectionFrame, viewport);
    handleSettle(state);
  };

  const deleteSelection = () => {
    const store = useFloorEngineStore.getState();
    const command = makeRemoveCommand(store.objectsById, store.selectedIds);
    if (command !== null) history.execute(command);
  };

  const duplicateSelection = () => {
    const store = useFloorEngineStore.getState();
    const result = makeDuplicateCommand(store.objectsById, store.selectedIds);
    if (result === null) return;
    history.execute(result.command);
    store.setSelectedIds(result.newIds);
  };

  const copySelection = () => {
    const store = useFloorEngineStore.getState();
    const objects: FloorObject[] = [];
    for (const id of store.selectedIds) {
      const object = store.objectsById[id];
      if (object !== undefined) objects.push(object);
    }
    store.setClipboard(objects);
  };

  const paste = () => {
    const store = useFloorEngineStore.getState();
    const result = makePasteCommand(store.clipboard);
    if (result === null) return;
    history.execute(result.command);
    store.setSelectedIds(result.newIds);
  };

  const groupSelection = () => {
    const store = useFloorEngineStore.getState();
    const objects: FloorObject[] = [];
    for (const id of store.selectedIds) {
      const object = store.objectsById[id];
      if (object !== undefined) objects.push(object);
    }
    const aabb = objectsAabb(objects);
    if (aabb === null) return;
    const result = makeGroupCommand(store.objectsById, store.selectedIds, aabb);
    if (result === null) return;
    const memberIds = store.selectedIds;
    history.execute(result.command);
    store.setSelectedIds([result.groupId, ...memberIds]);
  };

  const ungroupSelection = () => {
    const store = useFloorEngineStore.getState();
    const command = makeUngroupCommand(store.objectsById, store.selectedIds);
    if (command !== null) history.execute(command);
  };

  const cutSelection = () => {
    copySelection();
    deleteSelection();
  };

  /** Select every visible, unlocked object (Ctrl/Cmd+A standard). */
  const selectAll = () => {
    const store = useFloorEngineStore.getState();
    const ids: string[] = [];
    for (const object of Object.values(store.objectsById)) {
      if (object.visible && !object.locked) ids.push(object.id);
    }
    store.setSelectedIds(ids);
  };

  /**
   * Arrow-key nudge. Contiguous nudges coalesce into ONE history entry via
   * a per-selection mergeKey (Figma behavior — a burst of taps = one undo).
   */
  const nudgeSelection = (dx: number, dy: number) => {
    const store = useFloorEngineStore.getState();
    if (store.selectedIds.length === 0) return;
    const command = makeMoveCommand(
      store.objectsById,
      store.selectedIds,
      { x: dx, y: dy },
      `nudge:${store.selectedIds.join('|')}`,
    );
    if (command !== null) history.execute(command);
  };

  const reorderSelection = (direction: ZOrderDirection) => {
    const store = useFloorEngineStore.getState();
    const command = makeZOrderCommand(
      store.objectsById,
      store.layerObjectIds,
      store.selectedIds,
      direction,
    );
    if (command !== null) history.execute(command);
  };

  /**
   * Rotate the selection by `deltaDeg` around the selection-frame center —
   * the touch-friendly complement to the drag rotate handle (the toolbar's
   * tap-to-rotate-90° button). Successive taps coalesce into ONE undo step
   * via a selection-keyed mergeKey (the nudge precedent). Locked objects are
   * skipped (mirrors move/drag semantics).
   */
  const rotateSelection = (deltaDeg: number) => {
    const store = useFloorEngineStore.getState();
    const objects: FloorObject[] = [];
    for (const id of store.selectedIds) {
      const object = store.objectsById[id];
      if (object !== undefined && !object.locked) objects.push(object);
    }
    if (objects.length === 0) return;
    const pivot = objectsAabb(objects);
    if (pivot === null) return;
    const patches = rotationPatches(
      objects,
      { x: pivot.x + pivot.width / 2, y: pivot.y + pivot.height / 2 },
      deltaDeg,
    );
    const command = makeTransformCommand(
      store.objectsById,
      patches,
      `Rotate ${patches.length}`,
      `rotate:${store.selectedIds.join('|')}`,
    );
    if (command !== null) history.execute(command);
  };

  /** Zoom about the viewport center (toolbar buttons + Ctrl+/-). */
  const zoomBy = (factor: number) => {
    if (viewport === null) return;
    const snap = cameraSnapshot(camera);
    const next = zoomAboutPoint(
      snap,
      { x: viewport.width / 2, y: viewport.height / 2 },
      snap.zoom * factor,
    );
    applyCameraState(camera, next);
    handleSettle(next);
  };

  /** Jump to an absolute zoom about the viewport center (Ctrl+1 = 100%). */
  const zoomTo = (zoom: number) => {
    if (viewport === null) return;
    const snap = cameraSnapshot(camera);
    const next = zoomAboutPoint(
      snap,
      { x: viewport.width / 2, y: viewport.height / 2 },
      zoom,
    );
    applyCameraState(camera, next);
    handleSettle(next);
  };

  /** Esc — clear selection; a drawing/marquee tool drops back to EDIT. */
  const escape = () => {
    const store = useFloorEngineStore.getState();
    store.clearSelection();
    if (store.mode === FLOOR_MODE.DRAW || store.mode === FLOOR_MODE.SELECTION) {
      store.setMode(FLOOR_MODE.EDIT);
    }
  };

  const alignSelection = (edge: AlignEdge) => {
    const store = useFloorEngineStore.getState();
    const objects: FloorObject[] = [];
    for (const id of store.selectedIds) {
      const object = store.objectsById[id];
      if (object !== undefined) objects.push(object);
    }
    const patches = alignObjects(objects, edge);
    if (patches.length === 0) return;
    const command = makeTransformCommand(
      store.objectsById,
      patches,
      `Align ${edge}`,
    );
    if (command !== null) history.execute(command);
  };

  const distributeSelection = (axis: 'horizontal' | 'vertical') => {
    const store = useFloorEngineStore.getState();
    const objects: FloorObject[] = [];
    for (const id of store.selectedIds) {
      const object = store.objectsById[id];
      if (object !== undefined) objects.push(object);
    }
    const patches = distributeObjects(objects, axis);
    if (patches.length === 0) return;
    const command = makeTransformCommand(
      store.objectsById,
      patches,
      `Distribute ${axis}`,
    );
    if (command !== null) history.execute(command);
  };

  return {
    appearance,
    camera,
    gesture,
    /** Prebuilt pan for the selection-toolbar drag handle (chrome mounts it). */
    selectionDragGesture,
    gridPicture,
    staticPicture,
    selectedPicture,
    selectionFrame,
    zoomSnapshot,
    /** Read-only for chrome (toolbar visibility worklet) — never .set() it. */
    dragMode,
    dragDx,
    dragDy,
    dragSx,
    dragSy,
    anchorX,
    anchorY,
    dragRotation,
    rotCenterX,
    rotCenterY,
    guides,
    marquee,
    draft,
    history,
    handleSettle,
    /** Keyboard modifier channels — written by useFloorKeyboard (web). */
    modifiers: { shift: shiftSv, alt: altSv, space: spaceSv },
    ops: {
      fit,
      zoomToSelection,
      deleteSelection,
      duplicateSelection,
      copySelection,
      cutSelection,
      paste,
      groupSelection,
      ungroupSelection,
      alignSelection,
      distributeSelection,
      selectAll,
      nudgeSelection,
      reorderSelection,
      rotateSelection,
      zoomBy,
      zoomTo,
      escape,
    },
  };
};

export default useFloorEditor;
